import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LifeMenu } from "@/components/LifeMenu";
import { LIFE_CATEGORIES } from "@/lib/lifeCategories";
import { ThemeToggle } from "@/components/ThemeToggle";

export function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [lifeOpen, setLifeOpen] = useState(false);
  const [mLife, setMLife] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => { setLifeOpen(false); setOpen(false); }, [loc.pathname, loc.search]);

  return (
    <header className="sticky top-0 z-40 bg-[#0D0B10]/95 backdrop-blur-md border-b hairline">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="logo-link" className="font-display text-2xl md:text-3xl tracking-tight leading-none">
            THE CHEAPER WAY<span className="text-[#EF3340]">.</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link to="/find" data-testid="nav-find" className={`font-label text-xs tracking-widest link-hover ${loc.pathname === "/find" ? "text-[#EF3340]" : "text-stone-300"}`}>FIND</Link>
            <button onClick={() => setLifeOpen(!lifeOpen)} data-testid="nav-life"
              className={`font-label text-xs tracking-widest link-hover flex items-center gap-1 ${lifeOpen ? "text-[#EF3340]" : "text-stone-300"}`}>
              LIFE <ChevronDown className={`w-3 h-3 transition-transform ${lifeOpen ? "rotate-180" : ""}`} />
            </button>
            <Link to="/opportunities" data-testid="nav-opportunities" className={`font-label text-xs tracking-widest link-hover ${loc.pathname === "/opportunities" ? "text-[#EF3340]" : "text-stone-300"}`}>OPPORTUNITIES</Link>
            <Link to="/notes" data-testid="nav-notes" className={`font-label text-xs tracking-widest link-hover ${loc.pathname === "/notes" ? "text-[#EF3340]" : "text-stone-300"}`}>NOTES</Link>
            <Link to="/audit" data-testid="nav-audit" className={`font-label text-xs tracking-widest link-hover ${loc.pathname === "/audit" ? "text-[#EF3340]" : "text-stone-300"}`}>AUDIT</Link>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <ThemeToggle />
            <Link to="/pro" data-testid="nav-pro" className="font-label text-xs tracking-widest text-[#D4AF37] link-hover">PRO</Link>
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" data-testid="nav-admin" className="font-label text-xs tracking-widest text-stone-400 link-hover">ADMIN</Link>}
                <button onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn" className="font-label text-xs tracking-widest text-stone-400 link-hover">LOG OUT</button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login" className="font-label text-xs tracking-widest text-stone-300 link-hover">LOG IN</Link>
            )}
            <Link to="/find" data-testid="run-audit-cta" className="font-label text-xs tracking-widest bg-[#EF3340] text-white px-5 py-3 hover:bg-[#cc2a35] transition-colors">RUN AN AUDIT →</Link>
          </div>

          <button className="md:hidden text-stone-200" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">{open ? <X /> : <Menu />}</button>
        </div>
      </div>

      {lifeOpen && (
        <>
          <div className="hidden md:block fixed inset-0 top-20 z-30" onClick={() => setLifeOpen(false)} />
          <div className="hidden md:block relative"><LifeMenu onClose={() => setLifeOpen(false)} /></div>
        </>
      )}

      {open && (
        <div className="md:hidden border-t hairline bg-[#0D0B10] max-h-[85vh] overflow-auto">
          <div className="px-6 py-6 flex flex-col gap-4">
            <Link to="/find" className="font-label text-sm tracking-widest text-stone-200">FIND</Link>
            <button onClick={() => setMLife(!mLife)} data-testid="mnav-life" className="font-label text-sm tracking-widest text-stone-200 flex items-center gap-2 text-left">
              LIFE <ChevronDown className={`w-3 h-3 ${mLife ? "rotate-180" : ""}`} />
            </button>
            {mLife && (
              <div className="pl-3 flex flex-col gap-2 border-l hairline">
                {LIFE_CATEGORIES.map((c) => (
                  <Link key={c.slug} to={`/life/${c.slug}`} className="font-label text-xs tracking-widest text-stone-400">{c.name}</Link>
                ))}
              </div>
            )}
            <Link to="/opportunities" className="font-label text-sm tracking-widest text-stone-200">OPPORTUNITIES</Link>
            <Link to="/notes" className="font-label text-sm tracking-widest text-stone-200">NOTES</Link>
            <Link to="/audit" className="font-label text-sm tracking-widest text-stone-200">AUDIT</Link>
            <Link to="/pro" className="font-label text-sm tracking-widest text-[#D4AF37]">THE CHEAPER WAY PRO</Link>
            {user?.role === "admin" && <Link to="/admin" className="font-label text-sm tracking-widest text-stone-400">ADMIN</Link>}
            {user ? (
              <button onClick={async () => { await logout(); nav("/"); }} className="font-label text-sm tracking-widest text-stone-400 text-left">LOG OUT</button>
            ) : (
              <Link to="/login" className="font-label text-sm tracking-widest text-stone-200">LOG IN</Link>
            )}
            <Link to="/find" className="font-label text-xs tracking-widest bg-[#EF3340] text-white px-5 py-3 text-center">RUN AN AUDIT →</Link>
            <div className="pt-2 flex items-center gap-3">
              <span className="font-label text-[10px] tracking-widest text-stone-500">THEME</span>
              <ThemeToggle />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export function MobileTabBar() {
  const loc = useLocation();
  const tabs = [
    { label: "Find", to: "/find" },
    { label: "Life", to: "/life/secondhand" },
    { label: "Opps", to: "/opportunities" },
    { label: "Saved", to: "/notes" },
    { label: "Audit", to: "/audit" },
  ];
  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0D0B10] border-t hairline grid grid-cols-5">
      {tabs.map((t) => (
        <Link key={t.to} to={t.to} data-testid={`tab-${t.label.toLowerCase()}`}
          className={`py-3 text-center font-label text-[10px] tracking-widest ${loc.pathname === t.to ? "text-[#EF3340]" : "text-stone-400"}`}>
          {t.label.toUpperCase()}
        </Link>
      ))}
    </div>
  );
}
